import { useCallback, useEffect, useState } from 'react';
import type { AppSession } from '../lib/types';
import { ApiError, api } from '../lib/api';
import { startRedeemMandate, type AuthConfig } from '../lib/home';
import { rememberReturn } from '../lib/routes';
import { fmtAsset, progressLine, treasuryStage, type StakeResult, type TreasuryView } from '../lib/treasury';
import { shortAddress } from '../lib/format';
import { Card } from './Bits';

/**
 * Where a patient's HeartCoins live on chain: the treasury Smart Agent chartered under their person
 * agent at their Home. Earned coins are paid into it by the house; redemptions come out of it under
 * a mandate the patient signs. Nothing is faked: a play-coins deployment says so.
 */
export function TreasuryPanel({ session, config }: { session: AppSession; config: AuthConfig | null }) {
  const [view, setView] = useState<TreasuryView | null>(null);
  const [result, setResult] = useState<StakeResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await api.treasury(session.token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not read your coin account.');
    }
  }, [session.token]);

  useEffect(() => {
    void load();
  }, [load]);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.quickStart(session.token);
      setResult(r);
      if (r.next.action === 'create-at-home' && r.next.portalUrl) window.open(r.next.portalUrl, '_blank', 'noopener');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
      await load();
    }
  }, [session.token, load]);

  const authorise = useCallback(async () => {
    if (!config) return;
    setBusy(true);
    try {
      rememberReturn();
      location.href = await startRedeemMandate(config, view?.mandate.maxPerRedeem ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }, [config, view?.mandate.maxPerRedeem]);

  const stage = treasuryStage(view);
  const line = progressLine(result);

  return (
    <Card>
      <h3>Your coin account</h3>
      {stage === 'loading' ? <p className="hint">Reading…</p> : null}
      {stage === 'unavailable' ? (
        <>
          <p className="hint">This deployment keeps HeartCoins as play coins — they are real in the app, and they are not on chain yet.</p>
          <p className="hint small">{view?.unavailable}</p>
        </>
      ) : null}
      {view && stage !== 'unavailable' && stage !== 'loading' ? (
        <>
          <p className="hint">
            {view.chosen ? (
              <>
                Coins are held in <strong>{view.chosenName || shortAddress(view.chosen)}</strong>
                {view.balanceFormatted ? <> — {fmtAsset(view.balance, view.assetDecimals)} {view.assetSymbol} on chain</> : null}.
              </>
            ) : (
              <>Your HeartCoins will be paid into a treasury of your own — a Smart Agent your Home custodies. HeartCoach never holds it.</>
            )}
          </p>
          {view.notice ? <p className="notice">{view.notice}</p> : null}
          {stage === 'set-up' ? (
            <button className="primary" type="button" disabled={busy} onClick={() => void start()}>
              {busy ? 'Setting up…' : 'Set up my coin account'}
            </button>
          ) : null}
          {stage === 'authorise' ? (
            <>
              {view.mandate.problem ? <p className="form-error">{view.mandate.problem}</p> : null}
              <p className="hint">One thing left: your say-so for how many coins HeartCoach may redeem when you spend them. Your Home asks you and signs it.</p>
              <button className="primary" type="button" disabled={busy} onClick={() => void authorise()}>
                {busy ? 'Sending you to your Home…' : 'Authorise redemptions at your Home'}
              </button>
            </>
          ) : null}
          {stage === 'ready' ? <p className="hint good">Ready: earned coins land here, and redemptions are authorised.</p> : null}
          {result?.next.action === 'create-at-home' ? (
            <div className="handoff">
              <p className="hint">{result.next.said}</p>
              {result.next.portalUrl ? (
                <a className="button secondary" href={result.next.portalUrl} target="_blank" rel="noreferrer">
                  Open your Home →
                </a>
              ) : null}
              <button type="button" className="quiet" disabled={busy} onClick={() => void start()}>
                I've done it
              </button>
            </div>
          ) : null}
          {line ? <p className="hint">{line}</p> : null}
        </>
      ) : null}
      {error ? <p className="form-error">{error}</p> : null}
      {view?.chosen ? (
        <details className="fold">
          <summary>Details</summary>
          <dl className="kv">
            <dt>Treasury</dt>
            <dd className="mono">{view.chosen}</dd>
            <dt>Asset</dt>
            <dd className="mono">{view.asset || '—'}</dd>
            <dt>Chain</dt>
            <dd>{view.chainId}</dd>
            <dt>Redeem mandate</dt>
            <dd>{view.mandate.present ? `up to ${fmtAsset(view.mandate.maxPerRedeem, view.assetDecimals)} ${view.assetSymbol} a time` : 'not signed'}</dd>
          </dl>
        </details>
      ) : null}
    </Card>
  );
}
